import React, { useState, useEffect } from 'react';
import Marquee from 'react-fast-marquee';
import { Building2 } from 'lucide-react';
import api from '../api';

export default function CompanyLogoCarousel({ onSelect, selectedCompany }) {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch company logos from backend
    const fetchLogos = async () => {
      try {
        const response = await api.get('/jobs/logos');
        setLogos(response.data.logos || []);
      } catch (error) {
        console.error('Error fetching company logos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogos();
  }, []);

  if (loading) {
    return (
      <div className="w-full py-8 bg-slate-50 border-y border-slate-200">
        <div className="px-6 md:px-8">
          <div className="h-4 bg-slate-200 rounded w-48 mb-4 animate-pulse"></div>
          <div className="flex gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-32 h-10 bg-slate-200 rounded-full animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!logos.length) {
    return (
      <div className="w-full py-6 bg-slate-50 dark:bg-slate-800/50 border-y border-slate-200 dark:border-slate-700">
        <div className="text-center text-slate-500 dark:text-slate-400 text-sm">
          No companies with jobs found. Try refreshing or adjusting your search.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8 bg-slate-50 dark:bg-slate-800/50 border-y border-slate-200 dark:border-slate-700">
      <div className="mb-4 px-6 md:px-8 flex justify-between items-end">
        <div>
          <h2 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Top Hiring Companies ({logos.length})
          </h2>
        </div>
      </div>

      <Marquee gradient={true} gradientColor={[248, 250, 252]} speed={40}>
        <div className="flex gap-4 px-4">
          {logos.map((logoData, idx) => (
            <LogoItem
              key={`${logoData.company}-${idx}`}
              logoData={logoData}
              isSelected={selectedCompany === logoData.company}
              onSelect={onSelect}
            />
          ))}
        </div>
      </Marquee>
    </div>
  );
}

function LogoItem({ logoData, isSelected, onSelect }) {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <button
      onClick={() => onSelect && onSelect(logoData.company)}
      className={`
        flex items-center gap-3 px-4 py-2 rounded-full border transition-all duration-200 mx-2
        ${isSelected
          ? 'bg-indigo-600 border-indigo-600 text-white shadow-md scale-105'
          : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-200 hover:shadow-sm'
        }
      `}
    >
      <div className={`
        w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0
        ${isSelected ? 'bg-white/20' : 'bg-slate-50'}
      `}>
        {!imgFailed && logoData.logo ? (
          <img
            src={logoData.logo}
            alt={logoData.company}
            className="w-full h-full object-contain p-1"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Building2 className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
        )}
      </div>
      <span className="text-sm font-medium whitespace-nowrap pr-1">{logoData.company}</span>
    </button>
  );
}
